import { EventEmitter } from 'events';
import { IFluidObject } from '@fluidframework/core-interfaces';
import {
  IComponentProvidePresence,
  ComponentPresenceEvent,
  ComponentPresenceInformation,
  PresenceSelectionProps
} from '@fluidx/fluid-extension';
import { PostMessageHandlers, PostMessageSender } from '../Shared/PostMessage';
import {
  PresenceChangedId,
  PresenceChangedMessage,
  InitializePresenceInformationMessage,
  InitializePresenceInformationId
} from '../MessageTypes';

export function getHostSideComponentProvidePresence(): {
  component: IComponentProvidePresence;
  handlers: PostMessageHandlers<string>;
  dispose: () => void;
} {
  const component = new HostComponentPresence();

  const handlers: PostMessageHandlers<string> = {
    [InitializePresenceInformationId]: (message) => {
      const presenceInformation = (message as InitializePresenceInformationMessage).payload.presenceInformation;
      component.setPresenceInformation(presenceInformation);
    },
    [PresenceChangedId]: (message) => {
      const { clientId, presence } = (message as PresenceChangedMessage).payload;
      component.onPresenceChanged(clientId, presence);
    }
  };

  return {
    component,
    handlers,
    dispose: () => component.removeAllListeners()
  };
}

class HostComponentPresence extends EventEmitter implements IComponentProvidePresence {
  public get IComponentProvidePresence() {
    return this;
  }

  private presenceInformation: ComponentPresenceInformation = {};

  public setPresenceInformation(presenceInformation: ComponentPresenceInformation): void {
    this.presenceInformation = presenceInformation;
  }

  public getPresenceInformation(): ComponentPresenceInformation {
    return this.presenceInformation;
  }

  public onPresenceChanged(clientId: string, presence: PresenceSelectionProps | undefined): void {
    this.presenceInformation[clientId] = presence;
    this.emit(ComponentPresenceEvent.PresenceChanged, clientId, presence);
  }

  public dispose() {
    this.removeAllListeners();
  }
}

export function getGuestSideComponentProvidePresence(
  component: IFluidObject,
  sender: PostMessageSender
): PostMessageHandlers<string> | undefined {
  const presence = component.IComponentProvidePresence;
  if (!presence) {
    return undefined;
  }

  presence.on(ComponentPresenceEvent.PresenceChanged, (clientId, presence) => {
    const changedMessage: PresenceChangedMessage = {
      type: PresenceChangedId,
      payload: { clientId, presence }
    };
    sender.send(changedMessage);
  });

  // Populate the initial set of presence info
  const initializeMessage: InitializePresenceInformationMessage = {
    type: InitializePresenceInformationId,
    payload: { presenceInformation: presence.getPresenceInformation() }
  };
  sender.send(initializeMessage);

  // We don't expect a 2 way communication so there's no handler for this side of iframe.
  return undefined;
}
